import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { replaceCheckedEnvelopes, setIsAllEnvelopesSelected, toggleCheckedEnvelope } from '@store/modules/envelopes';

import { Nullable } from '@infologistics/frontend-libraries';
import type { Checkbox } from '@hooks/envelopes/types';
import type { IEnvelope } from '@store/modules/envelopes/types';

export const getEnvelopeCheckboxKey = (envelope: Pick<IEnvelope, 'oguid' | 'box'>): string =>
  `${envelope.box.oguid}:${envelope.oguid}`;

export const useCheckboxes = () => {
  const [headCheckbox, setHeadCheckboxState] = useState<Nullable<boolean>>(false)
  const {
    envelopes: { data },
    checkedEnvelopes
  } = useAppSelector(state => state.envelopes)
  const dispatch = useAppDispatch()

  const getCheckboxState = useCallback((checked: boolean): Checkbox => (
    data.reduce<Checkbox>((acc, envelope) => {
      acc[getEnvelopeCheckboxKey(envelope)] = checked

      return acc
    }, {})
  ), [data])

  const setAllCheckboxes = useCallback((checked: boolean): void => {
    dispatch(replaceCheckedEnvelopes(getCheckboxState(checked)))
  }, [dispatch, getCheckboxState])

  const setHeadCheckbox = useCallback((checked: boolean): void => {
    setHeadCheckboxState(checked)
    setAllCheckboxes(checked)
    dispatch(setIsAllEnvelopesSelected(checked))
  }, [dispatch, setAllCheckboxes])

  const selectedCheckbox = useCallback((oguid: string) => {
    setHeadCheckboxState(false)
    dispatch(toggleCheckedEnvelope(oguid))
  }, [dispatch])

  useEffect(() => {
    setHeadCheckboxState(false)
    dispatch(replaceCheckedEnvelopes(getCheckboxState(false)))
  }, [data, dispatch, getCheckboxState])

  return {
    headCheckbox,
    setHeadCheckbox,
    checkedEnvelopes,
    selectedCheckbox
  }
}
